import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, Copy, Crown, Share2 } from 'lucide-react';
import { theoriesData, TheoryChapter } from '../data/theories';
import { MarkdownRenderer } from './MarkdownContent';
import { ReaderSettingsMenu, useReaderDisplaySettings } from './ReaderDisplaySettings';
import XuanYuanTopBar from './XuanYuanTopBar';

interface TheoryReaderProps {
  onBack: () => void;
  intro?: string;
  canAccessPro?: boolean;
}

const TheoryReader: React.FC<TheoryReaderProps> = ({ onBack, intro, canAccessPro = false }) => {
  const [selectedChapter, setSelectedChapter] = useState<TheoryChapter | null>(null);
  const [showProModal, setShowProModal] = useState(false);
  const [shareNotice, setShareNotice] = useState('');
  const { settings, update, reset } = useReaderDisplaySettings('theory-reader', {
    paragraphSpacing: 14,
  });
  const WECHAT_ID = 'dao6257';

  const copyWechat = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(WECHAT_ID);
      } else {
        const temp = document.createElement('input');
        temp.value = WECHAT_ID;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      alert('微信号已复制');
    } catch (_) {
      alert('复制失败，请手动添加微信号：' + WECHAT_ID);
    }
  };

  const handleShareCatalog = async () => {
    const url = new URL('/theory', window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({ title: '轩辕国学 · 基础知识', url });
        return;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareNotice('链接已复制，可直接分享。');
        window.setTimeout(() => setShareNotice(''), 2200);
        return;
      }
      setShareNotice(url);
      window.setTimeout(() => setShareNotice(''), 3200);
    } catch {
      setShareNotice('分享失败，请稍后重试。');
      window.setTimeout(() => setShareNotice(''), 2200);
    }
  };

  if (selectedChapter) {
    return (
      <div className="min-h-screen bg-white flex flex-col animate-fade-in">
        <XuanYuanTopBar
          title="轩辕国学·基础知识"
          onBack={() => setSelectedChapter(null)}
          subtitle={selectedChapter.title}
          variant="light"
          right={(
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          )}
        />
        <div className="max-w-3xl mx-auto w-full p-4 sm:p-6 md:p-10 pb-16 sm:pb-20">
            <MarkdownRenderer content={selectedChapter.content} settings={settings} />
            <div className="mt-12 pt-8 border-t border-slate-100 text-center">
                <button onClick={() => setSelectedChapter(null)} className="px-8 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full font-bold transition-colors">
                    返回目录
                </button>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·基础知识"
        onBack={onBack}
        subtitle={shareNotice || intro}
        variant="light"
        right={(
          <div className="flex items-center gap-2">
            <button
              onClick={() => { void handleShareCatalog(); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              aria-label="分享基础知识目录"
            >
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">分享</span>
            </button>
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-8">
        <div className="max-w-4xl mx-auto">
            <div className="mb-4 text-center lg:text-left">
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-semibold">系统梳理</span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold">时间章节</span>
                  <span className="text-slate-600">从基础逻辑推演到核心底层思维</span>
                </div>
            </div>

            <div className="grid gap-2 md:gap-3 md:grid-cols-2 lg:grid-cols-2">
                {theoriesData.map((chapter, idx) => (
                    <button 
                        key={chapter.id} 
                        onClick={() => {
                          if (idx > 0 && !canAccessPro) {
                            setShowProModal(true);
                            return;
                          }
                          setSelectedChapter(chapter);
                        }}
                        className="group relative flex flex-col text-left bg-white p-2.5 sm:p-3.5 min-h-[84px] sm:min-h-[96px] rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-indigo-300 transition-all overflow-hidden"
                    >
                        <div className={`absolute top-0 right-0 w-20 h-20 -mr-6 -mt-6 rounded-full opacity-10 transition-transform group-hover:scale-150 ${chapter.color}`}></div>
                        
                        <div className="flex items-start justify-between gap-2 mb-1.5 relative z-10">
                            <h3 className="text-base sm:text-lg font-bold text-slate-800 group-hover:text-indigo-700 transition-colors relative z-10 line-clamp-1">
                              {idx + 1}. {chapter.title}
                            </h3>
                            <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-transform"/>
                        </div>
                        
                        <p className="text-xs sm:text-sm text-slate-500 leading-snug relative z-10 line-clamp-2">
                            {chapter.desc}
                        </p>
                        {idx > 0 && !canAccessPro && (
                          <div className="absolute top-2 right-2 rounded-md px-2 py-0.5 text-[10px] font-black tracking-wide text-slate-900 shadow-md ring-1 ring-amber-100/80 bg-gradient-to-r from-yellow-200 via-amber-300 to-yellow-100">
                            PRO
                          </div>
                        )}
                    </button>
                ))}
            </div>
        </div>
      </div>
      {showProModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative animate-fade-in-up p-6">
            <button onClick={() => setShowProModal(false)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
            </button>
            <div className="pr-8">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-amber-500" />
                <h3 className="text-xl font-bold text-slate-900">PRO 会员功能</h3>
              </div>
              <p className="text-slate-500 text-sm mb-4">第一章之外章节为 PRO 权限。</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left">
                <p className="text-sm text-slate-700 font-semibold mb-3">添加轩哥微信，为您开通 Pro 权限</p>
                <button onClick={copyWechat} className="inline-flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-700">
                  {WECHAT_ID}
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TheoryReader;
